import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Hash, Copy, Shuffle, TrendingUp, Users, Target } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { toast } from 'react-hot-toast';
import AdContainer from '../components/ads/AdContainer';
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig';

interface HashtagSuggestion {
  tag: string;
  popularity: 'High' | 'Medium' | 'Low';
  category: string;
  posts: number;
}

const HashtagGenerator: React.FC = () => {
  const [keyword, setKeyword] = useState('');
  const [platform, setPlatform] = useState<'instagram' | 'twitter' | 'tiktok' | 'linkedin'>('instagram');
  const [niche, setNiche] = useState<'general' | 'fitness' | 'food' | 'travel' | 'business' | 'fashion' | 'tech' | 'photography'>('general');
  const [hashtags, setHashtags] = useState<HashtagSuggestion[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedHashtags, setSelectedHashtags] = useState<string[]>([]);

  useSEO({
    title: 'Hashtag Generator - Generate Trending Hashtags for Social Media',
    description: 'Generate relevant hashtags for Instagram, Twitter, TikTok, and LinkedIn. Find trending hashtags to boost your social media reach and engagement.',
    keywords: 'hashtag generator, instagram hashtags, twitter hashtags, tiktok hashtags, social media hashtags, trending hashtags',
    canonical: 'https://snap-tools.online/social/hashtag-generator'
  });

  // Hashtag databases for different categories
  const hashtagDatabase = {
    general: ['life', 'love', 'instagood', 'photooftheday', 'beautiful', 'happy', 'cute', 'follow', 'followme', 'picoftheday'],
    fitness: ['fitness', 'gym', 'workout', 'fit', 'health', 'bodybuilding', 'motivation', 'training', 'exercise', 'fitlife'],
    food: ['food', 'foodie', 'delicious', 'yummy', 'foodporn', 'instafood', 'cooking', 'recipe', 'healthy', 'lunch'],
    travel: ['travel', 'wanderlust', 'adventure', 'vacation', 'explore', 'travelgram', 'instatravel', 'trip', 'nature', 'landscape'],
    business: ['business', 'entrepreneur', 'success', 'motivation', 'leadership', 'startup', 'marketing', 'growth', 'innovation', 'productivity'],
    fashion: ['fashion', 'style', 'ootd', 'outfit', 'shopping', 'trendy', 'fashionista', 'model', 'beauty', 'clothes'],
    tech: ['technology', 'tech', 'coding', 'programming', 'developer', 'software', 'innovation', 'digital', 'ai', 'startup'],
    photography: ['photography', 'photo', 'photographer', 'camera', 'art', 'nature', 'portrait', 'landscape', 'beautiful', 'creative']
  };

  // Platform-specific popular hashtags
  const platformHashtags = {
    instagram: ['reels', 'explore', 'viral', 'trending', 'instagram', 'insta', 'ig', 'post', 'story', 'igtv'],
    twitter: ['twitter', 'tweet', 'trending', 'viral', 'news', 'discussion', 'thread', 'retweet', 'follow', 'engagement'],
    tiktok: ['tiktok', 'fyp', 'foryou', 'viral', 'trending', 'funny', 'dance', 'challenge', 'duet', 'trend'],
    linkedin: ['linkedin', 'professional', 'career', 'networking', 'business', 'industry', 'leadership', 'growth', 'learning', 'success']
  };

  // Generate hashtags
  const generateHashtags = async () => {
    if (!keyword.trim()) {
      toast.error('Please enter a keyword');
      return;
    }

    setIsGenerating(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));

      const keywordLower = keyword.toLowerCase();
      const nicheHashtags = hashtagDatabase[niche];
      const platformSpecific = platformHashtags[platform];

      // Generate hashtag suggestions
      const suggestions: HashtagSuggestion[] = [];

      // Add keyword-based hashtags
      suggestions.push({
        tag: keywordLower.replace(/\s+/g, ''),
        popularity: 'High',
        category: 'Main Keyword',
        posts: Math.floor(Math.random() * 1000000) + 100000
      });

      // Add keyword variations
      const variations = [
        `${keywordLower}gram`,
        `${keywordLower}love`,
        `${keywordLower}life`,
        `daily${keywordLower}`,
        `${keywordLower}community`,
        `${keywordLower}addict`
      ];

      variations.forEach(variation => {
        suggestions.push({
          tag: variation.replace(/\s+/g, ''),
          popularity: Math.random() > 0.6 ? 'Medium' : 'High',
          category: 'Keyword Variation',
          posts: Math.floor(Math.random() * 500000) + 50000
        });
      });

      // Add niche hashtags
      nicheHashtags.slice(0, 8).forEach(tag => {
        suggestions.push({
          tag,
          popularity: Math.random() > 0.5 ? 'High' : 'Medium',
          category: 'Niche',
          posts: Math.floor(Math.random() * 2000000) + 200000
        });
      });

      // Add platform-specific hashtags
      platformSpecific.slice(0, 6).forEach(tag => {
        suggestions.push({
          tag,
          popularity: 'High',
          category: 'Platform',
          posts: Math.floor(Math.random() * 5000000) + 1000000
        });
      });

      // Add some low-competition hashtags
      const lowCompetition = [
        `${keywordLower}tips`,
        `${keywordLower}inspiration`,
        `${keywordLower}goals`,
        `${keywordLower}journey`,
        `${keywordLower}vibes`
      ];

      lowCompetition.forEach(tag => {
        suggestions.push({
          tag: tag.replace(/\s+/g, ''),
          popularity: 'Low',
          category: 'Low Competition',
          posts: Math.floor(Math.random() * 10000) + 1000
        });
      });

      setHashtags(suggestions);
      setSelectedHashtags([]);
      toast.success('Hashtags generated successfully!');
    } catch (error) {
      console.error('Failed to generate hashtags:', error);
      toast.error('Failed to generate hashtags');
    } finally {
      setIsGenerating(false);
    }
  };

  // Toggle hashtag selection
  const toggleHashtag = (tag: string) => {
    setSelectedHashtags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  // Copy selected hashtags
  const copySelectedHashtags = async () => {
    if (selectedHashtags.length === 0) {
      toast.error('Please select some hashtags first');
      return;
    }

    const hashtagText = selectedHashtags.map(tag => `#${tag}`).join(' ');
    
    try {
      await navigator.clipboard.writeText(hashtagText);
      toast.success(`Copied ${selectedHashtags.length} hashtags!`);
    } catch (error) {
      console.error('Failed to copy:', error);
      toast.error('Failed to copy hashtags');
    }
  };

  // Copy all hashtags
  const copyAllHashtags = async () => {
    if (hashtags.length === 0) {
      toast.error('Generate hashtags first');
      return;
    }

    const hashtagText = hashtags.map(h => `#${h.tag}`).join(' ');
    
    try {
      await navigator.clipboard.writeText(hashtagText);
      toast.success(`Copied ${hashtags.length} hashtags!`);
    } catch (error) {
      console.error('Failed to copy:', error);
      toast.error('Failed to copy hashtags');
    }
  };

  // Select top hashtags
  const selectTopHashtags = (count: number) => {
    const topHashtags = hashtags
      .filter(h => h.popularity === 'High')
      .slice(0, count)
      .map(h => h.tag);
    setSelectedHashtags(topHashtags);
    toast.success(`Selected top ${topHashtags.length} hashtags`);
  };

  // Get popularity color
  const getPopularityColor = (popularity: string) => {
    switch (popularity) {
      case 'High': return 'text-red-600 bg-red-100';
      case 'Medium': return 'text-yellow-600 bg-yellow-100';
      case 'Low': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  // Format number
  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 py-8">
      <div className="container mx-auto px-4 max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-2xl shadow-xl p-6 md:p-8"
        >
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
              <Hash className="w-8 h-8 text-blue-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Hashtag Generator</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Generate relevant and trending hashtags for your social media posts. Boost your reach and engagement across all platforms.
            </p>
          </div>

          <AdContainer 
            position="header"
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.header}
            className="mb-6"
          />

          {/* Generator Form */}
          <div className="bg-gray-50 rounded-lg p-6 mb-8">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Keyword/Topic
                  </label>
                  <input
                    type="text"
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    placeholder="Enter your main keyword (e.g., coffee, travel, fitness)"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    onKeyPress={(e) => e.key === 'Enter' && generateHashtags()}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Platform
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {(['instagram', 'twitter', 'tiktok', 'linkedin'] as const).map((p) => (
                      <button
                        key={p}
                        onClick={() => setPlatform(p)}
                        className={`p-3 rounded-lg border-2 transition-colors ${
                          platform === p
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                        }`}
                      >
                        {p.charAt(0).toUpperCase() + p.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Niche/Category
                  </label>
                  <select
                    value={niche}
                    onChange={(e) => setNiche(e.target.value as any)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="general">General</option>
                    <option value="fitness">Fitness & Health</option>
                    <option value="food">Food & Cooking</option>
                    <option value="travel">Travel & Adventure</option>
                    <option value="business">Business & Marketing</option>
                    <option value="fashion">Fashion & Style</option>
                    <option value="tech">Technology</option>
                    <option value="photography">Photography</option>
                  </select>
                </div>

                <button
                  onClick={generateHashtags}
                  disabled={isGenerating || !keyword.trim()}
                  className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                >
                  {isGenerating ? (
                    <>Generating...</>
                  ) : (
                    <>
                      <Shuffle className="w-4 h-4" />
                      Generate Hashtags
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Results */}
          {hashtags.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="space-y-6"
            >
              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3 justify-between items-center bg-gray-50 rounded-lg p-4">
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => selectTopHashtags(10)}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                  >
                    <Target className="w-4 h-4" />
                    Top 10
                  </button>
                  <button
                    onClick={() => selectTopHashtags(20)}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                  >
                    <TrendingUp className="w-4 h-4" />
                    Top 20
                  </button>
                  <button
                    onClick={() => selectTopHashtags(30)}
                    className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
                  >
                    <Users className="w-4 h-4" />
                    Top 30
                  </button>
                </div>
                
                <div className="flex gap-2">
                  <button
                    onClick={copySelectedHashtags}
                    disabled={selectedHashtags.length === 0}
                    className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 disabled:opacity-50 transition-colors flex items-center gap-2"
                  >
                    <Copy className="w-4 h-4" />
                    Copy Selected ({selectedHashtags.length})
                  </button>
                  <button
                    onClick={copyAllHashtags}
                    className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors flex items-center gap-2"
                  >
                    <Copy className="w-4 h-4" />
                    Copy All
                  </button>
                </div>
              </div>

              {/* Hashtags Grid */}
              <div className="grid gap-3">
                {hashtags.map((hashtag, index) => (
                  <div
                    key={index}
                    onClick={() => toggleHashtag(hashtag.tag)}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      selectedHashtags.includes(hashtag.tag)
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-lg font-medium text-gray-900">
                          #{hashtag.tag}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPopularityColor(hashtag.popularity)}`}>
                          {hashtag.popularity}
                        </span>
                        <span className="text-sm text-gray-500">
                          {hashtag.category}
                        </span>
                      </div>
                      
                      <div className="text-sm text-gray-600">
                        {formatNumber(hashtag.posts)} posts
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Selected Hashtags Preview */}
              {selectedHashtags.length > 0 && (
                <div className="bg-blue-50 rounded-lg p-4">
                  <h4 className="font-medium text-blue-900 mb-2">
                    Selected Hashtags ({selectedHashtags.length})
                  </h4>
                  <p className="text-sm text-blue-800 break-all">
                    {selectedHashtags.map(tag => `#${tag}`).join(' ')}
                  </p>
                </div>
              )}
            </motion.div>
          )}

          <AdContainer 
            position="footer"
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.footer}
            className="my-6"
          />

          {/* Educational Content */}
          <div className="grid md:grid-cols-2 gap-6 mt-8">
            <div className="bg-blue-50 rounded-lg p-6">
              <h4 className="font-semibold text-blue-900 mb-3">Hashtag Strategy</h4>
              <ul className="text-sm text-blue-800 space-y-2">
                <li>• Mix high, medium, and low competition hashtags</li>
                <li>• Use 10-30 hashtags per post (platform dependent)</li>
                <li>• Research your niche-specific hashtags</li>
                <li>• Create branded hashtags for your content</li>
                <li>• Monitor hashtag performance regularly</li>
              </ul>
            </div>

            <div className="bg-green-50 rounded-lg p-6">
              <h4 className="font-semibold text-green-900 mb-3">Platform Guidelines</h4>
              <ul className="text-sm text-green-800 space-y-2">
                <li>• <strong>Instagram:</strong> Up to 30 hashtags per post</li>
                <li>• <strong>Twitter:</strong> 1-2 hashtags work best</li>
                <li>• <strong>TikTok:</strong> 3-5 relevant hashtags</li>
                <li>• <strong>LinkedIn:</strong> 3-5 professional hashtags</li>
                <li>• Always check platform-specific trends</li>
              </ul>
            </div>

            <div className="bg-purple-50 rounded-lg p-6">
              <h4 className="font-semibold text-purple-900 mb-3">Best Practices</h4>
              <ul className="text-sm text-purple-800 space-y-2">
                <li>• Keep hashtags relevant to your content</li>
                <li>• Avoid banned or flagged hashtags</li>
                <li>• Use a mix of popular and niche hashtags</li>
                <li>• Create location-based hashtags when relevant</li>
                <li>• Update your hashtag strategy regularly</li>
              </ul>
            </div>

            <div className="bg-yellow-50 rounded-lg p-6">
              <h4 className="font-semibant text-yellow-900 mb-3">Engagement Tips</h4>
              <ul className="text-sm text-yellow-800 space-y-2">
                <li>• Research trending hashtags in your niche</li>
                <li>• Engage with posts using your hashtags</li>
                <li>• Create content around trending topics</li>
                <li>• Use hashtags in stories and comments too</li>
                <li>• Track which hashtags bring the most engagement</li>
              </ul>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default HashtagGenerator;
